<style scoped>
.headline {
  background-color: #1b2643;
  width: 100%;
  height: 60px;
  color: #ffffff;
}

.active_text {
  border-bottom: 2px solid #ffffff;
  height: 60px;
  font-size: 16px;
}

.bold-large-text {
  font-weight: bold;
  font-size: 18px;
}

.content {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
}
</style>

<template>
  <div class="mt-3">
    <v-row class="headline ml-n1">
      <v-col class="active_text d-flex align-center px-5">
        <p class="mb-0">{{ $t('packageResults.title') }}</p>
      </v-col>
    </v-row>
    <div v-if="error" class="content d-flex justify-start pa-10">
      <p>{{ $t('packageResults.packageNotFound') }}</p>
    </div>
    <div v-else class="content d-flex flex-column pa-10" style="gap: 40px">
      <div class="d-flex justify-end">
        <v-switch
          v-model="speakingTestOnly"
          :label="$t('packageResults.speakingTestOnly')"
        />
      </div>

      <div class="d-flex flex-column" style="gap: 20px">
        <span class="text-h6">{{ $t('packageResults.packageName') }}</span>
        {{ packageData?.name }} ({{ packageData?.code }})
      </div>

      <learner-population-data
        :data="learnerPopulationData"
        :speakingTestOnly="speakingTestOnly"
        :loading="loading"
      />

      <overview-by-category
        :speakingTestOnly="speakingTestOnly"
        :data="learnerPopulationData"
        :loading="loading"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import { packageService } from "@/api/packageService";
import LearnerPopulationData from "@/components/Dashboard/PackageResults/LearnerPopulationData.vue";
import OverviewByCategory from "@/components/Dashboard/PackageResults/OverviewByCategory.vue";
import type { CampaignResults, PackageWithId } from "@/resources/package";
import store from "@/store";
import { getProcessedPackageResults } from "@/utils/getProcessedPackageResults";
import { fetchScenarios } from "@/utils/scenarioUtils";

export default defineComponent({
  name: "PackageResults",

  components: {
    LearnerPopulationData,
    OverviewByCategory,
  },

  data() {
    return {
      speakingTestOnly: false,
      packageData: null as PackageWithId | null,
      campaignIds: [] as string[],
      learnerPopulationData: [] as CampaignResults[],
      loading: false,
      error: false,
    };
  },

  watch: {
    "$i18n.locale"() {
      this.updateDisplayNames();
    },
  },

  methods: {
    updateDisplayNames() {
      // Update displayNames in all rows across all campaigns
      this.learnerPopulationData.forEach((campaign) => {
        campaign.rows.forEach((row) => {
          // Re-translate deleted learner text to current locale
          // Check for existing translations in any language
          if (
            row.displayName === "Deleted learner" ||
            row.displayName === "削除されたユーザー"
          ) {
            row.displayName = this.$t(
              "campaignResult.deletedLearner",
            ) as string;
          }
        });
      });
    },
    async loadPackageData() {
      try {
        const customerId = store.state.customerId as unknown as string;
        const scenarioList = await fetchScenarios();
        const packageCode = this.$route.params.packageCode;

        this.packageData = await packageService.getByIdOrCode(
          packageCode as string,
        );

        if (
          this.packageData === null ||
          this.packageData.customerId !== customerId
        ) {
          throw new Error(
            "No package found and/or package not owned by the customer",
          );
        }

        if (this.packageData?.id) {
          this.learnerPopulationData = await getProcessedPackageResults(
            this.packageData,
            scenarioList,
          );
        }
      } catch (error) {
        console.error("error:", error);
        this.error = true;
      }
    },
  },

  async mounted() {
    this.loading = true;
    await this.loadPackageData();
    this.loading = false;
  },
});
</script>
